import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Column,
  CustomTableComponent,
} from "../../../shared/elements/customTable.component";
import { PagerComponent } from "../../../shared/elements/pager.component";
import { CustomButtonComponent } from "../../../shared/elements/cutomBtn.component";
import {
  useGetAboutHook,
  useGetConversationsHook,
  useGetConversationThreadHook,
  useGetFeedbacksHook,
  useSendMessageHook,
  useUpdateAboutHook,
} from "../hooks/support.hook";

type TabType = "chat" | "avis" | "about";

const TABS: { key: TabType; label: string }[] = [
  { key: "chat", label: "Chat support" },
  { key: "avis", label: "Les avis" },
  { key: "about", label: "A propos de nous" },
];

const feedbackColumns: Column[] = [
  {
    key: "clientName",
    label: "Client",
    render: (_, row) => (
      <Box>
        <Typography fontWeight="600" fontSize="0.8rem">
          {row.clientName}
        </Typography>
        <Typography fontSize="0.7rem" color="gray">
          {row.clientEmail}
        </Typography>
      </Box>
    ),
  },
  {
    key: "rating",
    label: "Note",
    render: (value) => <Typography fontWeight="600">{value}</Typography>,
  },
  {
    key: "comment",
    label: "Commentaire",
    render: (value) => <Typography fontSize="0.8rem">{value}</Typography>,
  },
];

export const SupportComponent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("chat");

  return (
    <Box display="flex" height="100%" flexDirection="column">
      <Box sx={{ pb: 1, width: { xs: "100%", md: "60%" } }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          {TABS.map((tab) => (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                flex: 1,
                textAlign: "center",
                py: 1,
                px: 1.5,
                borderRadius: "16px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                transition: "all 0.3s ease",
                ...(activeTab === tab.key
                  ? {
                      background:
                        "linear-gradient(135deg, #B0006D 0%, #E6007A 100%)",
                      color: "#fff",
                      boxShadow: "0 4px 12px rgba(176, 0, 109, 0.3)",
                    }
                  : {
                      background: "transparent",
                      color: "#666",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }),
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Box>
      </Box>

      {activeTab === "chat" && <ChatSupportTab />}
      {activeTab === "avis" && <FeedbacksTab />}
      {activeTab === "about" && <AboutTab />}
    </Box>
  );
};

const ChatSupportTab: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: conversations } = useGetConversationsHook(1, 20);
  const { data: thread, isLoading: threadLoading } =
    useGetConversationThreadHook(selectedUserId || "");
  const sendMessageMutation = useSendMessageHook();

  useEffect(() => {
    if (!selectedUserId && conversations?.data.length) {
      setSelectedUserId(conversations.data[0].userId.toString());
    }
  }, [conversations, selectedUserId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages]);

  const filteredConversations = (conversations?.data || []).filter((c) =>
    c.fullName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedUserId) return;
    const content = messageInput;
    setMessageInput("");
    sendMessageMutation.mutate({ userId: selectedUserId, content });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: { xs: "auto", md: "70vh" },
      }}
    >
      {/* Left Sidebar - Chat List */}
      <Box
        sx={{
          width: { xs: "100%", md: "220px" },
          flexShrink: 0,
          height: { xs: "220px", md: "auto" },
          backgroundColor: "#FFFDF7",
          display: "flex",
          flexDirection: "column",
          borderRadius: "10px",
        }}
      >
        {/* Search Bar */}
        <Box sx={{ px: 1.5, pb: 1.5, pt: 2 }}>
          <TextField
            fullWidth
            placeholder="Recherche"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#bbb", fontSize: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                backgroundColor: "#f9f9f9",
                "& fieldset": {
                  borderColor: "#e8e8e8",
                },
                "&:hover fieldset": {
                  borderColor: "#d0d0d0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#B0006D",
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: "8px 12px",
                fontSize: "13px",
                "&::placeholder": {
                  color: "#bbb",
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* Users List */}
        <List sx={{ flex: 1, overflow: "auto", px: 1, py: 0 }}>
          {filteredConversations.map((conversation) => (
            <ListItem
              key={conversation.userId}
              onClick={() => setSelectedUserId(conversation.userId.toString())}
              sx={{
                borderRadius: "10px",
                mb: 0.3,
                cursor: "pointer",
                py: 0.8,
                px: 1,
                backgroundColor:
                  selectedUserId === conversation.userId.toString()
                    ? "#f9f9f9"
                    : "transparent",
                "&:hover": {
                  backgroundColor: "#f9f9f9",
                },
              }}
            >
              <ListItemAvatar sx={{ minWidth: 38 }}>
                <Avatar
                  src={conversation.avatar || ""}
                  alt={conversation.fullName}
                  sx={{ width: 30, height: 30 }}
                >
                  {conversation.fullName.charAt(0)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#222",
                      mb: 0.3,
                    }}
                  >
                    {conversation.fullName}
                  </Typography>
                }
                secondary={
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#888",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {conversation.isLastMessageFromAdmin && conversation.lastMessage
                      ? `Vous: ${conversation.lastMessage}`
                      : conversation.lastMessage || ""}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Right Side - Chat Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFFDF7",
          borderRadius: "10px",
          ml: { xs: 0, md: 1.5 },
          mt: { xs: 1.5, md: 0 },
          minHeight: { xs: "400px", md: "auto" },
        }}
      >
        {selectedUserId && thread ? (
          <>
            {/* Chat Header */}
            <Box
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
              }}
            >
              <Avatar
                src={thread.user.avatar || ""}
                sx={{ width: 34, height: 34 }}
              >
                {thread.user.fullName.charAt(0)}
              </Avatar>
              <Typography
                sx={{ fontWeight: 600, fontSize: "13px", color: "#333" }}
              >
                {thread.user.fullName}
              </Typography>
            </Box>

            <Divider
              sx={{
                display: "block",
                margin: "0 auto",
                width: "95%",
              }}
            />

            {/* Messages Area */}
            <Box sx={{ flex: 1, p: 2, overflow: "auto" }}>
              {thread.messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    justifyContent: message.isFromAdmin
                      ? "flex-end"
                      : "flex-start",
                  }}
                >
                  {!message.isFromAdmin && (
                    <Avatar
                      src={thread.user.avatar || ""}
                      sx={{ mr: 1, width: 26, height: 26 }}
                    >
                      {thread.user.fullName.charAt(0)}
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      maxWidth: { xs: "80%", sm: "60%" },
                      backgroundColor: message.isFromAdmin
                        ? "#A0005C"
                        : "#fff",
                      color: message.isFromAdmin ? "#fff" : "#333",
                      borderRadius: "12px",
                      p: 1.5,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography sx={{ fontSize: "12px" }}>
                      {message.content}
                    </Typography>
                  </Box>
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Box>

            {/* Message Input */}
            <Box
              sx={{
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <TextField
                  fullWidth
                  placeholder="Ecrivez votre message"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  maxRows={2}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "24px",
                      backgroundColor: "#FFFDF7",
                      "& fieldset": {
                        borderColor: "#e8e8e8",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#B0006D",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "9px 16px",
                      fontSize: "13px",
                      "&::placeholder": {
                        color: "#ccc",
                        opacity: 1,
                      },
                    },
                  }}
                />
                <Box
                  onClick={handleSendMessage}
                  sx={{
                    cursor: messageInput.trim() ? "pointer" : "not-allowed",
                    opacity: messageInput.trim() ? 1 : 0.5,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#E6007A",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s",
                    "&:hover": messageInput.trim()
                      ? {
                          color: "#B0006D",
                        }
                      : {},
                  }}
                >
                  Envoyer
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ color: "#999", fontSize: "16px" }}>
              {threadLoading
                ? "Chargement..."
                : "Sélectionnez une conversation pour commencer"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const FeedbacksTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useGetFeedbacksHook(page);

  return (
    <Box mt={1.5}>
      {isLoading && (
        <Box p={2} textAlign="center" color="#999" fontSize="0.85rem">
          Chargement...
        </Box>
      )}
      {!isLoading && error && (
        <Box p={2} textAlign="center" color="#c0392b" fontSize="0.85rem">
          Erreur lors du chargement des avis.
        </Box>
      )}
      {data && (
        <>
          <CustomTableComponent columns={feedbackColumns} rows={data.data} />
          <PagerComponent
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      )}
    </Box>
  );
};

const AboutTab: React.FC = () => {
  const { data, isLoading, error } = useGetAboutHook();
  const updateAboutMutation = useUpdateAboutHook();
  const [content, setContent] = useState("");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (data && !initialized) {
      setContent(data.content);
      setInitialized(true);
    }
  }, [data, initialized]);

  const handleSave = () => {
    updateAboutMutation.mutate(content);
  };

  return (
    <Box mt={1.5} sx={{ backgroundColor: "#FFFDF7", borderRadius: "12px", p: 2 }}>
      {isLoading && (
        <Box p={2} textAlign="center" color="#999" fontSize="0.85rem">
          Chargement...
        </Box>
      )}
      {!isLoading && error && (
        <Box p={2} textAlign="center" color="#c0392b" fontSize="0.85rem">
          Erreur lors du chargement du contenu.
        </Box>
      )}
      {!isLoading && !error && (
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            multiline
            minRows={12}
            fullWidth
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Contenu de la page A propos de nous"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#fff",
                fontSize: "13px",
              },
            }}
          />
          <Box display="flex" justifyContent="flex-end">
            <CustomButtonComponent
              label={
                updateAboutMutation.isPending ? "Enregistrement..." : "Enregistrer"
              }
              onClick={handleSave}
              disabled={updateAboutMutation.isPending}
              sx={{ width: "160px" }}
            />
          </Box>
          {updateAboutMutation.isSuccess && (
            <Typography fontSize="0.75rem" color="green">
              Contenu enregistré.
            </Typography>
          )}
          {updateAboutMutation.isError && (
            <Typography fontSize="0.75rem" color="#c0392b">
              Erreur lors de l'enregistrement.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
